import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const slides = Array.from({ length: 19 }, (_, i) =>
  `/dossier/slide_${String(i + 1).padStart(2, "0")}.jpg`
);

const DownloadCard = ({ kind, label, ext, info, accent }) => {
  const file = info?.[kind];
  const url = `${API}/dossier/download/${kind}`;
  return (
    <a
      href={url}
      download={file?.filename}
      data-testid={`download-${kind}-btn`}
      className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-stone-300/60 bg-[#FAFAF6] p-6 transition-all hover:-translate-y-1 hover:shadow-xl"
      style={{ borderTopColor: accent, borderTopWidth: 3 }}
    >
      <div>
        <p className="font-serif text-xs uppercase tracking-[0.35em] text-stone-500">
          Descargar
        </p>
        <h3 className="mt-2 font-serif text-3xl text-stone-900">{label}</h3>
        <p className="mt-3 text-sm leading-relaxed text-stone-600">{ext}</p>
      </div>
      <div className="mt-6 flex items-center justify-between border-t border-stone-300/70 pt-4">
        <span className="text-xs uppercase tracking-widest text-stone-500">
          {file?.size_kb ? `${(file.size_kb / 1024).toFixed(2)} MB` : "—"}
        </span>
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full transition-transform group-hover:translate-x-1"
          style={{ backgroundColor: accent }}
        >
          <svg
            className="h-4 w-4 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  );
};

const DossierPage = () => {
  const [info, setInfo] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dossier/info`).then((r) => setInfo(r.data)).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-gb-bg font-serif text-stone-900">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-stone-300/70">
        <div className="mx-auto max-w-6xl px-8 py-16 md:py-24">
          <p
            className="mb-6 text-xs uppercase tracking-[0.4em] text-stone-500"
            data-testid="brand-eyebrow"
          >
            Dossier reeditado · Tipografía unificada
          </p>
          <h1
            className="font-serif text-5xl leading-[1.05] tracking-tight text-stone-900 md:text-7xl"
            data-testid="hero-title"
          >
            Lo que <em className="italic text-[#26775E]">dejamos</em> atrás
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600 md:text-lg">
            Un documental de Eduardo Moratalla. Hemos rehecho el dossier original
            unificando la tipografía y el estilo gráfico, manteniendo intactas
            todas las imágenes y textos. Disponible como PowerPoint
            editable y PDF rediseñado.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="downloads">
            <DownloadCard
              kind="pptx"
              label="PowerPoint editable"
              ext=".pptx · totalmente editable en PowerPoint, Keynote o Google Slides (subir y abrir)"
              info={info}
              accent="#26775E"
            />
            <DownloadCard
              kind="pdf"
              label="PDF rediseñado"
              ext=".pdf · listo para enviar a productoras, festivales o coproductores"
              info={info}
              accent="#8a5a3b"
            />
          </div>

          <p className="mt-6 text-xs uppercase tracking-[0.3em] text-stone-500">
            Para Google Slides → Drive → Subir archivo → Abrir con Google Slides
          </p>
        </div>
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#26775E]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#8a5a3b]/10 blur-3xl" />
      </header>

      {/* What changed */}
      <section className="mx-auto max-w-6xl px-8 py-16" data-testid="changes-section">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-stone-500">01</p>
            <h3 className="mt-3 font-serif text-2xl">Tipografía unificada</h3>
            <p className="mt-3 text-sm leading-relaxed text-stone-600">
              Una sola pareja tipográfica a lo largo de todo el dossier:
              <em> Calibri</em> para titulares y eyebrows, <em>Cambria</em> para el cuerpo
              y las citas. Jerarquía consistente, kerning cuidado y mismo
              line-height en todas las páginas.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-stone-500">02</p>
            <h3 className="mt-3 font-serif text-2xl">Sistema gráfico coherente</h3>
            <p className="mt-3 text-sm leading-relaxed text-stone-600">
              Mismo color de tinta, mismo azul mediterráneo de acento, misma
              línea-rúbrica de 1,2 pt bajo cada título, misma numeración de
              página, mismo footer. Paleta arena/azul mar preservada.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-stone-500">03</p>
            <h3 className="mt-3 font-serif text-2xl">Contenido intacto</h3>
            <p className="mt-3 text-sm leading-relaxed text-stone-600">
              Las 19 páginas y todas las fotografías originales se conservan
              tal cual. Cada elemento del .pptx es editable: texto, imágenes,
              líneas, números de página.
            </p>
          </div>
        </div>
      </section>

      {/* Preview gallery */}
      <section className="border-t border-stone-300/70 bg-[#F1EEE5]/40 py-16" data-testid="preview-section">
        <div className="mx-auto max-w-6xl px-8">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-stone-500">
                Previsualización
              </p>
              <h2 className="mt-2 font-serif text-3xl md:text-4xl">
                19 páginas · diseño unificado
              </h2>
            </div>
            <p className="hidden text-xs uppercase tracking-widest text-stone-500 md:block">
              Haz clic sobre cualquier slide para ampliar
            </p>
          </div>

          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
            data-testid="thumbnail-grid"
          >
            {slides.map((src, i) => (
              <button
                key={i}
                onClick={() => setActive(src)}
                data-testid={`slide-thumb-${i + 1}`}
                className="group relative aspect-[16/9] overflow-hidden rounded-md bg-stone-200 ring-1 ring-stone-300 transition-all hover:ring-2 hover:ring-[#26775E]"
              >
                <img
                  src={src}
                  alt={`Slide ${i + 1}`}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute bottom-1 right-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  {String(i + 1).padStart(2, "0")} / 19
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-300/70 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-8 text-xs uppercase tracking-[0.3em] text-stone-500 md:flex-row">
          <span data-testid="footer-brand">LO QUE DEJAMOS ATRÁS · DOSSIER</span>
          <span>Reedición tipográfica · 2026</span>
        </div>
      </footer>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setActive(null)}
          data-testid="lightbox"
        >
          <img
            src={active}
            alt="slide preview"
            className="max-h-full max-w-full rounded shadow-2xl"
          />
          <button
            onClick={() => setActive(null)}
            data-testid="lightbox-close"
            className="absolute right-6 top-6 rounded-full bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

function App() {
  return null;
}

export default DossierPage;
